import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import LoadingButton from "@mui/lab/LoadingButton";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { workspacesApi } from "../../actions";
import businessFlowsTheme, { brand } from "../../theme/businessFlowsTheme";
import ViewAttributes from "./ViewAttributes";
import ViewPreviewPanel from "./ViewPreviewPanel";
import { Caution, Hint, Refusal, Surface, dialogPaper, focusRing, menuPaper, quietDanger, rowSx } from "./WorkspaceUi";
import {
  BUILTIN_VIEW_NOTE,
  TYPE_ACTIONS,
  applyTypeAction,
  attributeNames,
  catalogueCountsNotice,
  copyAttributesFrom,
  editorDefaultDatabase,
  isBuiltInView,
  sortTypes,
  staleBannerText,
  startingAttributes,
  viewFile,
  viewStale,
} from "./workspaceFormat";

// What the editor says it does, in one sentence.
export const EDITOR_RULE =
  "People who hold this view see only the object types switched on here, and only the details listed for each. The name is always shown.";

// Kept for the view file and for anything that reorders a list of details.
export const moveAttribute = (list, from, to) => {
  // "name" stays first: nothing moves into or out of position 0.
  if (from < 1 || to < 1 || from >= list.length || to >= list.length || from === to) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

// The view as the editor holds it: every type's details start with "name".
const editable = (definition) =>
  (definition?.objects || []).map((object) => ({
    ...object,
    attributes: ["name", ...(object.attributes || []).filter((a) => a !== "name")],
  }));

function TypeRow({ first, entry, object, missing, isNew, chosen, disabled, selectable, selected, onSelect, onToggle, onChoose, onMenu, onRemove }) {
  const on = Boolean(object);
  const label = object?.label || entry?.label || object?.type || entry?.type;
  const type = entry?.type || object?.type;
  const objects = entry?.objects || 0;
  // A missing type's row says nothing more: its section already does.
  const details = missing
    ? ""
    : [
        objects ? `${objects} ${objects === 1 ? "object" : "objects"}` : "No objects in this database",
        on ? `${object.attributes.length} ${object.attributes.length === 1 ? "detail" : "details"} shown` : null,
      ]
        .filter(Boolean)
        .join(" · ");
  return (
    <Box
      sx={{
        ...rowSx(first),
        py: 1.25,
        opacity: missing ? 0.7 : 1,
        bgcolor: chosen ? brand.shell : "transparent",
        borderLeft: chosen ? `3px solid ${brand.ink}` : "3px solid transparent",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        {selectable && (
          <Checkbox
            size="small"
            checked={selected}
            disabled={disabled}
            onChange={(e) => onSelect(type, e.target.checked)}
            inputProps={{ "aria-label": `Select ${label}` }}
            sx={{ p: 0.5 }}
          />
        )}
        {!missing && (
          <Switch
            checked={on}
            disabled={disabled}
            onChange={(e) => onToggle(type, e.target.checked)}
            inputProps={{ "aria-label": `Show ${label}` }}
            size="small"
          />
        )}
        <Box
          sx={{ minWidth: 0, flex: 1, cursor: missing ? "default" : "pointer" }}
          role={missing ? undefined : "button"}
          tabIndex={missing ? undefined : 0}
          aria-label={missing ? undefined : `Details of ${label}`}
          onClick={missing ? undefined : () => onChoose(type)}
          onKeyDown={
            missing
              ? undefined
              : (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onChoose(type);
                  }
                }
          }
        >
          <Stack direction="row" spacing={0.75} alignItems="baseline" flexWrap="wrap" useFlexGap>
            <Typography sx={{ fontWeight: 650, overflowWrap: "anywhere", color: on ? brand.ink : brand.muted }}>{label}</Typography>
            {label !== type && (
              <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                {type}
              </Typography>
            )}
            {isNew && <Chip size="small" label="New" sx={{ height: 20, fontWeight: 700, bgcolor: brand.signal, color: brand.ink }} />}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {details}
          </Typography>
        </Box>
        {missing
          ? !disabled && (
              <Button size="small" sx={quietDanger} aria-label={`Remove ${label} from the view`} onClick={() => onRemove(type)}>
                Remove
              </Button>
            )
          : !disabled && (
              <Tooltip title="More for this object type">
                <IconButton size="small" aria-label={`Options for ${label}`} onClick={(e) => onMenu(e.currentTarget, type)}>
                  <MoreHorizRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
      </Stack>
    </Box>
  );
}

function Section({ title, hint, children }) {
  return (
    <Box>
      {title && <Typography sx={{ fontWeight: 700, mb: hint ? 0.25 : 1 }}>{title}</Typography>}
      {hint && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {hint}
        </Typography>
      )}
      <Surface>{children}</Surface>
    </Box>
  );
}

// One view: which object types a group sees, and for each the details, in
// order. The database only supplies the types, their details and their
// counts; it isn't saved into the view, and types it lacks stay in the view.
export default function ViewEditor({ open, workspace, viewId, initialDatabase, mayChange, onClose, onSaved, onCopy }) {
  const phone = useMediaQuery(businessFlowsTheme.breakpoints.down("sm"));
  const narrow = useMediaQuery(businessFlowsTheme.breakpoints.down("md"));
  const databases = workspace.databases || [];
  const [view, setView] = useState(null);
  const [objects, setObjects] = useState([]);
  const saved = useRef("[]");
  const held = useRef(null); // the view this editor already read
  // Opens on the view's own database (`initialDatabase`, passed by the tab -
  // it already knows it from the list). A fresh ViewEditor is mounted every
  // time a view is opened (ViewsTab renders it only while `editing` is set),
  // so this initialiser reruns on every open and re-settles there - nothing
  // is remembered from the last time this or another view was edited.
  const [database, setDatabase] = useState(() => editorDefaultDatabase(initialDatabase, databases));
  const [catalogue, setCatalogue] = useState(null);
  const [catalogueError, setCatalogueError] = useState("");
  const [reading, setReading] = useState(0); // bumped by Refresh
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("name");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [selected, setSelected] = useState([]);
  const [rowMenu, setRowMenu] = useState(null); // { anchor, type }
  const [copyMenu, setCopyMenu] = useState(null); // { anchor, to }
  const [chosen, setChosen] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);

  // The built-in view is resolved against a database, so it is re-read when
  // the database changes; a stored view is read once, keeping unsaved work.
  useEffect(() => {
    let live = true;
    const again = held.current && held.current.id === viewId;
    if (again && !isBuiltInView(held.current)) return undefined;
    (async () => {
      const res = await workspacesApi.view(workspace.id, viewId, database);
      if (!live) return;
      if (!res.success) {
        setError(res.msg || "Couldn't load the view.");
        return;
      }
      const list = editable(res.content.definition);
      held.current = res.content;
      saved.current = JSON.stringify(list);
      setView(res.content);
      setObjects(list);
    })();
    return () => {
      live = false;
    };
  }, [workspace.id, viewId, database]);

  useEffect(() => {
    if (!database) return undefined;
    let live = true;
    setCatalogue(null);
    setCatalogueError("");
    (async () => {
      const res = await workspacesApi.viewCatalogue(workspace.id, database);
      if (!live) return;
      if (res.success) setCatalogue(res.content);
      else setCatalogueError(res.msg || "Couldn't read this database's object types.");
    })();
    return () => {
      live = false;
    };
  }, [workspace.id, database, reading]);

  const builtIn = isBuiltInView(view);
  const disabled = !mayChange || !view || builtIn;
  const dirty = Boolean(view) && !builtIn && JSON.stringify(objects) !== saved.current;
  const byType = useMemo(() => new Map(objects.map((o) => [o.type, o])), [objects]);

  const types = useMemo(() => catalogue?.types || [], [catalogue]);
  const known = useMemo(() => new Set(types.map((t) => t.type)), [types]);
  const entryOf = useCallback((type) => types.find((t) => t.type === type) || null, [types]);
  const available = useMemo(() => Object.fromEntries(types.map((t) => [t.type, attributeNames(t)])), [types]);
  const stale = useMemo(() => (catalogue ? viewStale(objects, types) : { newTypes: [], missingTypes: [] }), [catalogue, objects, types]);
  const isNew = useMemo(() => new Set(stale.newTypes), [stale.newTypes]);

  const needle = query.trim().toLowerCase();
  const matches = (type, label) => !needle || [type, label].some((v) => String(v || "").toLowerCase().includes(needle));
  const listed = (t) => matches(t.type, byType.get(t.type)?.label || t.label);
  const labelOf = (t) => byType.get(t.type)?.label || t.label || t.type;
  // The root is reachable anyway (the explorer starts there), so it's only
  // listed when a view already names it.
  const regular = sortTypes(types.filter((t) => !t.isDiagram && (!t.isRoot || byType.has(t.type)) && listed(t)), sort, labelOf);
  const diagrams = sortTypes(types.filter((t) => t.isDiagram && listed(t)), sort, labelOf);
  const missing = catalogue || !database ? objects.filter((o) => !known.has(o.type) && matches(o.type, o.label)) : [];
  const databaseLabel = catalogue?.label || databases.find((d) => d.name === database)?.label || database;
  const shownCount = objects.filter((o) => known.has(o.type)).length;
  const offered = types.filter((t) => !t.isDiagram && !t.isRoot).length;

  const toggle = (type, on) => {
    if (on) setObjects((list) => (list.some((o) => o.type === type) ? list : [...list, { type, attributes: startingAttributes(available[type] || []) }]));
    else setObjects((list) => list.filter((o) => o.type !== type));
    setChosen(type);
  };
  const change = (type, attributes) => setObjects((list) => list.map((o) => (o.type === type ? { ...o, attributes } : o)));
  const remove = (type) => setObjects((list) => list.filter((o) => o.type !== type));

  const select = (type, on) => setSelected((list) => (on ? [...new Set([...list, type])] : list.filter((t) => t !== type)));
  const selectable = !disabled;
  const selectableTypes = [...regular, ...diagrams].map((t) => t.type);
  const allSelected = selectableTypes.length > 0 && selectableTypes.every((type) => selected.includes(type));

  const runAction = (action, over) => {
    setObjects((list) => applyTypeAction(list, action, over, available));
    setSelected([]);
  };

  const addAllNew = () => setObjects((list) => applyTypeAction(list, "basics", stale.newTypes, available));

  const close = () => {
    if (busy) return;
    if (mayChange && dirty) setDiscarding(true);
    else onClose();
  };

  const save = async () => {
    setBusy(true);
    setError("");
    const res = await workspacesApi.saveView(workspace.id, viewId, { definition: viewFile(view.name, objects) });
    setBusy(false);
    if (!res.success) {
      setError(res.msg || "Couldn't save the view.");
      return;
    }
    saved.current = JSON.stringify(objects);
    onSaved(res.msg || `View ${view.name} saved.`);
  };

  // entry: a catalogue type, or (isMissing) the view's own object.
  const row = (entry, index, isMissing = false) => (
    <TypeRow
      key={entry.type}
      first={!index}
      entry={isMissing ? null : entry}
      object={byType.get(entry.type)}
      missing={isMissing}
      isNew={isNew.has(entry.type)}
      chosen={chosen === entry.type}
      disabled={disabled}
      selectable={selectable && !isMissing}
      selected={selected.includes(entry.type)}
      onSelect={select}
      onToggle={toggle}
      onChoose={setChosen}
      onMenu={(anchor, type) => setRowMenu({ anchor, type })}
      onRemove={remove}
    />
  );

  const chosenType = chosen || objects[0]?.type || regular[0]?.type || "";
  const chosenEntry = entryOf(chosenType);
  const chosenObject = byType.get(chosenType) || null;
  const chosenLabel = chosenObject?.label || chosenEntry?.label || chosenType;

  const side = (
    <Stack spacing={2.5}>
      <ViewAttributes
        entry={chosenEntry}
        object={chosenObject}
        label={chosenType ? chosenLabel : ""}
        disabled={disabled}
        otherTypes={objects.filter((o) => o.type !== chosenType).map((o) => ({ type: o.type, label: o.label || entryOf(o.type)?.label || o.type }))}
        onChange={(attributes) => change(chosenType, attributes)}
        onToggleType={(on) => toggle(chosenType, on)}
        onCopyFrom={(from) => setObjects((list) => copyAttributesFrom(list, from, [chosenType]))}
        onRefresh={() => setReading((n) => n + 1)}
      />
      {narrow ? (
        <Box>
          <Button size="small" onClick={() => setPreviewOpen((was) => !was)}>
            {previewOpen ? "Hide preview" : "Show preview"}
          </Button>
          {previewOpen && (
            <Box sx={{ mt: 1.5 }}>
              <ViewPreviewPanel viewName={view?.name || ""} objects={objects} types={types} previewType={chosenType} onPreviewType={setChosen} />
            </Box>
          )}
        </Box>
      ) : (
        <ViewPreviewPanel viewName={view?.name || ""} objects={objects} types={types} previewType={chosenType} onPreviewType={setChosen} />
      )}
    </Stack>
  );

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="lg" fullScreen={phone} PaperProps={dialogPaper}>
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <span>{view ? view.name : <Skeleton width={180} />}</span>
          {builtIn && <Chip size="small" label="Built-in" sx={{ height: 22, fontWeight: 700, bgcolor: brand.canvas, border: `1px solid ${brand.line}` }} />}
        </Stack>
      </DialogTitle>
      <DialogContent sx={focusRing}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {EDITOR_RULE}
        </Typography>

        {builtIn && (
          <Hint
            sx={{ mb: 2 }}
            title="This view keeps itself up to date"
            action={
              mayChange && onCopy ? (
                <Button size="small" onClick={() => onCopy(view)} sx={{ flexShrink: 0 }}>
                  Copy to change
                </Button>
              ) : null
            }
          >
            {BUILTIN_VIEW_NOTE} It can't be changed — copy it to make one of your own.
          </Hint>
        )}

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2 }} alignItems={{ sm: "flex-start" }}>
          {databases.length > 0 && (
            <TextField
              select
              size="small"
              label="Object types from"
              value={database}
              onChange={(e) => setDatabase(e.target.value)}
              helperText="Only for choosing. Not saved in the view."
              sx={{ minWidth: 220 }}
            >
              {databases.map((d) => (
                <MenuItem key={d.name} value={d.name}>
                  {d.label || d.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            size="small"
            placeholder="Find an object type"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            inputProps={{ "aria-label": "Find an object type" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, maxWidth: { sm: 280 } }}
          />
          <TextField select size="small" label="Sort by" value={sort} onChange={(e) => setSort(e.target.value)} sx={{ minWidth: 150 }}>
            <MenuItem value="name">Name</MenuItem>
            <MenuItem value="objects">Objects</MenuItem>
          </TextField>
          <Tooltip title="Read this database's object types again">
            <span>
              <Button size="small" startIcon={<RefreshRoundedIcon />} disabled={!database} onClick={() => setReading((n) => n + 1)}>
                Refresh
              </Button>
            </span>
          </Tooltip>
        </Stack>

        {databases.length === 0 && <Hint sx={{ mb: 2 }}>This workspace has no databases yet, so there are no object types to choose from.</Hint>}
        {catalogueError && <Refusal sx={{ mb: 2 }}>{catalogueError}</Refusal>}
        {catalogue && catalogueCountsNotice(catalogue.counts) && <Hint sx={{ mb: 2 }}>{catalogueCountsNotice(catalogue.counts)}</Hint>}

        {/* The picker can look at any library, but the view itself belongs to
            one - say so, plainly, whenever they've drifted apart, so nobody
            edits against the wrong catalogue without noticing. */}
        {!builtIn && view?.database && database && database !== view.database && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Showing {databaseLabel}'s object types; this view is for {view.databaseLabel || view.database}.
          </Typography>
        )}

        {!disabled && stale.newTypes.length > 0 && (
          <Caution
            sx={{ mb: 2 }}
            title="New object types in this database"
            action={
              <Button size="small" variant="outlined" onClick={addAllNew} sx={{ flexShrink: 0 }}>
                Add all new
              </Button>
            }
          >
            {staleBannerText(stale.newTypes, databaseLabel)} Adding them shows their name and description.
          </Caution>
        )}

        <Stack direction={{ xs: "column", md: "row" }} spacing={2.5} alignItems="flex-start">
          <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
            {!disabled && selectableTypes.length > 0 && (
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={!allSelected && selected.length > 0}
                  onChange={(e) => setSelected(e.target.checked ? selectableTypes : [])}
                  inputProps={{ "aria-label": "Select all object types" }}
                  sx={{ p: 0.5 }}
                />
                {selected.length === 0 ? (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ flex: "1 1 22rem", minWidth: "16rem" }}>
                      {`${shownCount} of ${offered} object types shown`}
                    </Typography>
                    <Button size="small" onClick={() => runAction("show", regular.map((t) => t.type))}>
                      Show all
                    </Button>
                    <Button size="small" onClick={() => runAction("hide", regular.map((t) => t.type))}>
                      Hide all
                    </Button>
                  </>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ fontWeight: 650 }}>
                      {selected.length} selected
                    </Typography>
                    {TYPE_ACTIONS.map((action) => (
                      <Button
                        key={action.key}
                        size="small"
                        variant="outlined"
                        aria-label={`${action.label} for the selected object types`}
                        onClick={() => runAction(action.key, selected)}
                      >
                        {action.label}
                      </Button>
                    ))}
                    <Button size="small" onClick={() => setSelected([])}>
                      Clear
                    </Button>
                  </>
                )}
              </Stack>
            )}

            {!view || (database && !catalogue && !catalogueError) ? (
              <Stack spacing={1}>
                {[0, 1, 2, 3].map((i) => (
                  <Skeleton key={i} variant="rounded" height={52} sx={{ borderRadius: 3 }} />
                ))}
              </Stack>
            ) : (
              <Stack spacing={2.5}>
                {regular.length > 0 && <Section>{regular.map((t, i) => row(t, i))}</Section>}
                {catalogue && regular.length === 0 && !needle && <Hint>This database has no object types yet.</Hint>}
                {diagrams.length > 0 && (
                  <Section title="Diagrams" hint="Off unless you want them listed as objects. A visible object's diagrams open anyway.">
                    {diagrams.map((t, i) => row(t, i))}
                  </Section>
                )}
                {missing.length > 0 && (
                  <Section title="Not in this database" hint="Kept in the view for databases that have them. Remove one if it is gone for good.">
                    {missing.map((o, i) => row(o, i, true))}
                  </Section>
                )}
                {needle && regular.length + diagrams.length + missing.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No object type matches “{query.trim()}”.
                  </Typography>
                )}
              </Stack>
            )}
          </Box>

          <Box sx={{ width: { xs: "100%", md: 380 }, flexShrink: 0 }}>{side}</Box>
        </Stack>

        {error && <Refusal sx={{ mt: 2 }}>{error}</Refusal>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1, pl: 1 }}>
          {view ? `${objects.length} ${objects.length === 1 ? "object type" : "object types"} shown${dirty ? " · not saved yet" : ""}` : ""}
        </Typography>
        <Button onClick={close} disabled={busy}>
          {mayChange && !builtIn ? "Cancel" : "Close"}
        </Button>
        {mayChange && !builtIn && (
          <LoadingButton variant="contained" loading={busy} disabled={!view || !dirty} onClick={save}>
            Save view
          </LoadingButton>
        )}
      </DialogActions>

      <Menu anchorEl={rowMenu?.anchor} open={Boolean(rowMenu)} onClose={() => setRowMenu(null)} {...menuPaper}>
        {TYPE_ACTIONS.map((action) => (
          <MenuItem
            key={action.key}
            onClick={() => {
              runAction(action.key, [rowMenu.type]);
              setRowMenu(null);
            }}
          >
            {action.label}
          </MenuItem>
        ))}
        <MenuItem
          disabled={!byType.has(rowMenu?.type) || objects.length < 2}
          onClick={() => {
            // Anchored on the row's own button: the menu item is gone by then.
            setCopyMenu({ anchor: rowMenu.anchor, to: rowMenu.type });
            setRowMenu(null);
          }}
        >
          Copy details from…
        </MenuItem>
      </Menu>

      <Menu anchorEl={copyMenu?.anchor} open={Boolean(copyMenu)} onClose={() => setCopyMenu(null)} {...menuPaper}>
        {objects
          .filter((o) => o.type !== copyMenu?.to)
          .map((o) => (
            <MenuItem
              key={o.type}
              onClick={() => {
                setObjects((list) => copyAttributesFrom(list, o.type, [copyMenu.to]));
                setCopyMenu(null);
              }}
            >
              {o.label || entryOf(o.type)?.label || o.type}
            </MenuItem>
          ))}
      </Menu>

      <Dialog open={discarding} onClose={() => setDiscarding(false)} fullWidth maxWidth="xs" PaperProps={dialogPaper}>
        <DialogTitle sx={{ fontWeight: 700 }}>Discard your changes?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            The view stays as it was last saved.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDiscarding(false)}>Keep editing</Button>
          <Button variant="contained" color="error" onClick={onClose}>
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
